import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Button, Select } from '@loyalup/ui';

import { getClientFields } from 'components/ClientFields';

import { ReactComponent as EditIcon } from 'icons/edit.svg';
import { ReactComponent as MarkIcon } from 'icons/mark-2.svg';
import { ReactComponent as DelIcon } from 'icons/del.svg';

import s from './ClientImportColumn.module.css';

const MAX_ROWS = Array.from(Array(5).keys());

const ClientImportColumn = forwardRef(({ column, disabled, className, onChange }, ref) => {
  const listOfFields = getClientFields({ asList: true });
  const fields = getClientFields();
  const [t] = useTranslation();
  const [editing, setEditing] = useState(false);
  const defaultColumnName = t('Столбец {{index}}', column.index);

  return (
    <div
      ref={ref}
      className={cn(s.root, className, {
        [s.editing]: editing,
        [s.skipped]: column.skipped,
        [s.defined]: !!column.field && !column.skipped && !editing,
        [s.undefined]: column.error,
      })}
    >
      {!editing && (
        <div className={s.title} onClick={() => setEditing(true)}>
          {(column.field && fields[column.field].label(t)) || column.label || defaultColumnName}
        </div>
      )}
      {editing && <div className={cn(s.title, s.hint)}>{t('Название столбца')}</div>}
      {!editing && !column.error && (
        <div onClick={() => setEditing(true)} className={s.binding}>
          {column.field ? `@${column.field}` : t('столбец не определен')}
        </div>
      )}
      {!editing && column.error && <div className={s.error}>{t('Укажите столбец')}</div>}
      {editing && (
        <div className={s.select}>
          <Select
            stretch
            fixedWidth={195}
            theme="toolbar-white"
            items={listOfFields.map(item => ({ value: item.name, ...item }))}
            placeholder={t('Выберите столбец')}
            value={column.field}
            onChange={value => {
              onChange({ ...column, field: value, error: false });
              setEditing(false);
            }}
          />
        </div>
      )}
      <div className={s.buttons}>
        {!editing && !column.skipped && (
          <Button
            className={s.button}
            theme="toolbar-transparent"
            icons="first"
            disabled={disabled}
            onClick={() => setEditing(true)}
          >
            <EditIcon />
            {t('Изменить')}
          </Button>
        )}
        {editing && (
          <Button
            className={s.button}
            theme="toolbar-transparent"
            icons="first"
            disabled={disabled}
            onClick={() => setEditing(false)}
          >
            <MarkIcon />
            {t('Сохранить')}
          </Button>
        )}
        {column.skipped && (
          <Button
            className={s.button}
            theme="toolbar-transparent"
            icons="first"
            disabled={disabled}
            onClick={() => onChange({ ...column, skipped: false })}
          >
            <DelIcon />
            {t('Восстановить')}
          </Button>
        )}
        {!column.skipped && (
          <Button
            className={s.button}
            theme="toolbar-transparent"
            icons="first"
            disabled={disabled}
            onClick={() => {
              setEditing(false);
              onChange({ ...column, skipped: true, error: false });
            }}
          >
            <DelIcon />
            {t('Удалить')}
          </Button>
        )}
      </div>
      <div className={s.table}>
        <div className={s.row}>{column.label || defaultColumnName}</div>
        {MAX_ROWS.map((_, index) => (
          <div key={index} className={s.row}>
            {column.rows[index]}&nbsp;
          </div>
        ))}
      </div>
    </div>
  );
});

ClientImportColumn.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  column: PropTypes.shape({
    index: PropTypes.number,
    field: PropTypes.string,
    label: PropTypes.string,
    skipped: PropTypes.bool,
    error: PropTypes.bool,
    rows: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default ClientImportColumn;
